import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactsRolesPanel = ({ contacts, onAdd, onUpdate, onRemove }) => {
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    isPrimary: false
  });
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: 'primary', label: 'Primary Contact' },
    { value: 'sales', label: 'Sales Contact' },
    { value: 'technical', label: 'Technical Contact' },
    { value: 'finance', label: 'Finance Contact' },
    { value: 'escalation', label: 'Escalation Contact' }
  ];

  const validateContact = () => {
    const newErrors = {};
    if (!newContact?.name?.trim()) newErrors.name = 'Name is required';
    if (!newContact?.email?.trim()) newErrors.email = 'Email is required';
    if (!newContact?.role) newErrors.role = 'Role is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newContact?.email && !emailRegex?.test(newContact?.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleAddContact = () => {
    if (validateContact()) {
      onAdd({ ...newContact, id: Date.now(), verified: false });
      setNewContact({ name: '', email: '', phone: '', role: '', isPrimary: false });
      setIsAddingContact(false);
      setErrors({});
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'primary': return 'Star';
      case 'sales': return 'TrendingUp';
      case 'technical': return 'Wrench';
      case 'finance': return 'DollarSign';
      case 'escalation': return 'AlertCircle';
      default: return 'User';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'primary': return 'bg-primary/10 text-primary';
      case 'sales': return 'bg-success/10 text-success';
      case 'technical': return 'bg-accent/10 text-accent';
      case 'finance': return 'bg-warning/10 text-warning';
      case 'escalation': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-16">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground font-heading">
            Contacts & Roles
          </h3>
          <p className="text-sm text-muted-foreground mt-4">
            Add key contacts for partner communication
          </p>
        </div>
        {!isAddingContact && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            onClick={() => setIsAddingContact(true)}
          >
            Add Contact
          </Button>
        )}
      </div>
      {isAddingContact && (
        <div className="bg-muted/50 border border-border rounded-lg p-16 md:p-24 space-y-16">
          <div className="flex items-center justify-between mb-12">
            <h4 className="text-sm font-semibold text-foreground">New Contact</h4>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              iconSize={16}
              onClick={() => {
                setIsAddingContact(false);
                setNewContact({ name: '', email: '', phone: '', role: '', isPrimary: false });
                setErrors({});
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter contact name"
              required
              value={newContact?.name}
              onChange={(e) => setNewContact({ ...newContact, name: e?.target?.value })}
              error={errors?.name}
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="contact@example.com"
              required
              value={newContact?.email}
              onChange={(e) => setNewContact({ ...newContact, email: e?.target?.value })}
              error={errors?.email}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+1 (555) 000-0000"
              value={newContact?.phone}
              onChange={(e) => setNewContact({ ...newContact, phone: e?.target?.value })}
            />

            <Select
              label="Role"
              placeholder="Select role"
              required
              options={roleOptions}
              value={newContact?.role}
              onChange={(value) => setNewContact({ ...newContact, role: value })}
              error={errors?.role}
            />
          </div>

          <div className="flex items-center justify-end gap-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAddingContact(false);
                setNewContact({ name: '', email: '', phone: '', role: '', isPrimary: false });
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Check"
              iconPosition="left"
              iconSize={16}
              onClick={handleAddContact}
            >
              Add Contact
            </Button>
          </div>
        </div>
      )}
      {contacts?.length === 0 && !isAddingContact ? (
        <div className="text-center py-32 border-2 border-dashed border-border rounded-lg">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-12" />
          <p className="text-sm text-muted-foreground">No contacts added yet</p>
          <p className="text-xs text-muted-foreground caption mt-4">
            Add at least one primary contact to continue
          </p>
        </div>
      ) : (
        <div className="space-y-12">
          {contacts?.map((contact) => (
            <div
              key={contact?.id}
              className="bg-card border border-border rounded-lg p-16 hover:shadow-elevation-sm transition-smooth"
            >
              <div className="flex items-start justify-between gap-12">
                <div className="flex items-start gap-12 flex-1 min-w-0">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                    ${getRoleBadgeColor(contact?.role)}
                  `}>
                    <Icon name={getRoleIcon(contact?.role)} size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-8 mb-4">
                      <h4 className="text-sm font-semibold text-foreground truncate">
                        {contact?.name}
                      </h4>
                      {contact?.verified && (
                        <Icon name="BadgeCheck" size={16} className="text-success flex-shrink-0" />
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-8">
                        <Icon name="Mail" size={14} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground caption truncate">
                          {contact?.email}
                        </span>
                      </div>
                      {contact?.phone && (
                        <div className="flex items-center gap-8">
                          <Icon name="Phone" size={14} className="text-muted-foreground flex-shrink-0" />
                          <span className="text-xs text-muted-foreground caption">
                            {contact?.phone}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-8">
                      <span className={`
                        inline-flex items-center gap-4 px-8 py-4 rounded-md
                        text-xs font-medium caption
                        ${getRoleBadgeColor(contact?.role)}
                      `}>
                        {roleOptions?.find(r => r?.value === contact?.role)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {!contact?.verified && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Mail"
                      iconSize={16}
                      onClick={() => onUpdate(contact?.id, { ...contact, verified: true })}
                    >
                      <span className="hidden md:inline">Verify</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    iconName="Trash2"
                    iconSize={16}
                    onClick={() => onRemove(contact?.id)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactsRolesPanel;